"use client";

import { useCallback, useEffect, useState } from "react";

// 화면 전체 폰트 크기 — <html> font-size(rem 기준값)를 조절한다.
// 모든 텍스트가 rem 토큰(text-3xs/2xs/xs/sm/base/lg/xl/2xl)이라 함께 스케일된다.
const MIN_PX = 13;
const MAX_PX = 20;
const DEFAULT_PX = 16;
const STORAGE_KEY = "ui-font-px";

function clampPx(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_PX;
  return Math.min(MAX_PX, Math.max(MIN_PX, Math.round(n)));
}

function applyFontPx(px: number): void {
  if (typeof document !== "undefined") {
    document.documentElement.style.fontSize = `${px}px`;
  }
}

export function FontSizeControl({ className = "" }: { className?: string }) {
  const [px, setPx] = useState<number>(DEFAULT_PX);

  // 마운트 시 저장값 동기화 (FOUC 방지 인라인 스크립트가 이미 <html>에 적용했더라도 state 일치)
  useEffect(() => {
    let stored = DEFAULT_PX;
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v != null) stored = clampPx(Number(v));
    } catch {}
    setPx(stored);
    applyFontPx(stored);
  }, []);

  const update = useCallback((next: number) => {
    const v = clampPx(next);
    setPx(v);
    applyFontPx(v);
    try { localStorage.setItem(STORAGE_KEY, String(v)); } catch {}
  }, []);

  const btnCls =
    "w-7 h-7 flex items-center justify-center rounded-md border border-black/[0.12] bg-white text-black/70 " +
    "hover:text-neutral-900 hover:border-black/[0.25] active:bg-black/[0.04] disabled:opacity-35 disabled:cursor-default " +
    "transition-colors text-xs font-semibold leading-none";

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <button type="button" aria-label="폰트 작게" onClick={() => update(px - 1)} disabled={px <= MIN_PX} className={btnCls}>
        A−
      </button>
      <span className="min-w-[2.75rem] text-center text-2xs text-black/55 tabular-nums select-none" aria-live="polite">
        {px}px
      </span>
      <button type="button" aria-label="폰트 크게" onClick={() => update(px + 1)} disabled={px >= MAX_PX} className={btnCls}>
        A+
      </button>
    </div>
  );
}
